import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AdminUserDetail as AdminUserDetailType,
  AdminUserPlatform,
  getAdminUserDetail,
} from '../api';

export default function AdminUserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<AdminUserDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchUser() {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const detail = await getAdminUserDetail(Number(id));
        setData(detail);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load user');
      } finally {
        setLoading(false);
      }
    }

    fetchUser();
  }, [id]);

  if (loading) {
    return (
      <div className="admin-user-detail">
        <div className="card">
          <p>Loading user...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="admin-user-detail">
        <button className="person-back-button" onClick={() => navigate('/admin')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Back to Admin
        </button>
        <div className="card">
          <p className="error">{error || 'User not found'}</p>
        </div>
      </div>
    );
  }

  const { user, stats, platforms, playlists } = data;

  return (
    <div className="admin-user-detail">
      <button className="person-back-button" onClick={() => navigate('/admin')}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 18l-6-6 6-6" />
        </svg>
        Back to Admin
      </button>

      {/* User header */}
      <div className="admin-user-header">
        {user.picture ? (
          <img src={user.picture} alt="" className="admin-user-avatar" />
        ) : (
          <div className="admin-user-avatar placeholder">
            {user.name?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
          </div>
        )}
        <div className="admin-user-info">
          <h2 className="admin-user-name">{user.name || '(No name)'}</h2>
          {user.username && <span className="admin-user-username">@{user.username}</span>}
          <span className="admin-user-email">{user.email}</span>
          <span className="admin-user-joined">
            Joined {new Date(user.created_at).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Stats cards */}
      <div className="stats-hero" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="stats-card">
          <div className="stats-card-value">{stats.playlist_count}</div>
          <div className="stats-card-label">Playlists</div>
        </div>
        <div className="stats-card">
          <div className="stats-card-value">{stats.movie_count}</div>
          <div className="stats-card-label">Movies</div>
        </div>
        <div className="stats-card">
          <div className="stats-card-value">{stats.watched_count}</div>
          <div className="stats-card-label">Watched</div>
        </div>
        <div className="stats-card">
          <div className="stats-card-value">{stats.friend_count}</div>
          <div className="stats-card-label">Friends</div>
        </div>
      </div>

      {/* Platforms */}
      <h3 className="admin-section-title">Platforms ({platforms.length})</h3>

      {platforms.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0, opacity: 0.6 }}>No platform data yet.</p>
        </div>
      ) : (
        <div className="admin-platform-list">
          {platforms.map((p) => (
            <PlatformCard key={p.platform} platform={p} />
          ))}
        </div>
      )}

      {/* Playlists */}
      <h3 className="admin-section-title">Playlists ({playlists.length})</h3>

      {playlists.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0, opacity: 0.6 }}>No playlists yet.</p>
        </div>
      ) : (
        <div className="admin-playlist-list">
          {playlists.map((playlist) => (
            <Link
              key={playlist.id}
              to={`/playlists/${playlist.id}`}
              className="admin-playlist-item"
            >
              <div className="admin-playlist-item-header">
                <span className="admin-playlist-name">{playlist.name}</span>
                {playlist.type && (
                  <span className="admin-playlist-badge">
                    {playlist.type === 'want_to_watch'
                      ? 'Want to Watch'
                      : playlist.type === 'watched'
                        ? 'Watched'
                        : playlist.type}
                  </span>
                )}
              </div>
              {playlist.prompt && (
                <p className="admin-playlist-prompt">
                  {playlist.prompt.length > 120
                    ? playlist.prompt.slice(0, 120) + '...'
                    : playlist.prompt}
                </p>
              )}
              <div className="admin-playlist-meta">
                <span>{playlist.movie_count} movies</span>
                <span className={`admin-playlist-status status-${playlist.status}`}>
                  {playlist.status}
                </span>
                <span>{new Date(playlist.created_at).toLocaleDateString()}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function PlatformCard({ platform }: { platform: AdminUserPlatform }) {
  const isIos = platform.platform === 'ios';
  const label = isIos ? 'iOS' : 'Web';

  return (
    <div className="admin-platform-card">
      <div className="admin-platform-card-header">
        <span className="admin-platform-icon">{isIos ? '\u{f8ff}' : '\u{1F310}'}</span>
        <span className="admin-platform-label">{label}</span>
        <span className="admin-platform-last-seen">
          Last seen {new Date(platform.last_seen_at).toLocaleDateString()}
        </span>
      </div>

      {isIos && (platform.device_model || platform.os_version || platform.app_version) && (
        <div className="admin-platform-details">
          {platform.device_model && (
            <div className="admin-platform-detail-row">
              <span className="admin-platform-detail-label">Device</span>
              <span className="admin-platform-detail-value">{platform.device_model}</span>
            </div>
          )}
          {platform.os_version && (
            <div className="admin-platform-detail-row">
              <span className="admin-platform-detail-label">OS</span>
              <span className="admin-platform-detail-value">{platform.os_version}</span>
            </div>
          )}
          {platform.app_version && (
            <div className="admin-platform-detail-row">
              <span className="admin-platform-detail-label">App Version</span>
              <span className="admin-platform-detail-value">{platform.app_version}</span>
            </div>
          )}
          {platform.locale && (
            <div className="admin-platform-detail-row">
              <span className="admin-platform-detail-label">Locale</span>
              <span className="admin-platform-detail-value">{platform.locale}</span>
            </div>
          )}
        </div>
      )}

      <div className="admin-platform-first-seen">
        First seen {new Date(platform.created_at).toLocaleDateString()}
      </div>
    </div>
  );
}
